import { apiGet } from "../api-client"

/**
 * Filing metrics response from backend
 */
export interface FilingMetrics {
  active_filings: number
  requires_action: number
  avg_time_to_submission_seconds: number | null
  rejection_rate: number
  total_submitted: number
  total_rejected: number
}

/**
 * Filing breakdown by form type
 */
export interface FilingBreakdownItem {
  form_type: string
  count: number
  by_status: Record<string, number>
}

export interface FilingBreakdown {
  breakdown: FilingBreakdownItem[]
}

/**
 * Timeline breakdown by period
 */
export type TimelineEvent = "created" | "submitted" | "approved" | "rejected" | "rejected_and_submitted"
export type TimePeriod = "day" | "week" | "month" | "quarter" | "year"

export interface TimelineBreakdownItem {
  form_type: string
  count: number
}

export interface TimelinePeriod {
  date: string
  breakdown: TimelineBreakdownItem[]
}

export interface FilingBreakdownTimeline {
  period: TimePeriod
  event: TimelineEvent
  timeline: TimelinePeriod[]
}

/**
 * Fetch filing metrics for the dashboard
 */
export async function fetchFilingMetrics(): Promise<FilingMetrics> {
  const response = await apiGet<FilingMetrics>("/api/filings/metrics/")
  return response
}

/**
 * Fetch filing breakdown by form type
 */
export async function fetchFilingBreakdown(): Promise<FilingBreakdown> {
  const response = await apiGet<FilingBreakdown>("/api/filings/breakdown/")
  return response
}

/**
 * Fetch filing breakdown timeline with filtering
 */
export async function fetchFilingBreakdownTimeline(
  period: TimePeriod = "month",
  event: TimelineEvent = "created",
  formTypes?: string[],
  days?: number
): Promise<FilingBreakdownTimeline> {
  const params = new URLSearchParams()
  params.set("period", period)
  params.set("event", event)
  
  if (formTypes && formTypes.length > 0) {
    formTypes.forEach((ft) => params.append("form_type", ft))
  }
  
  if (days) {
    params.set("days", days.toString())
  }

  const url = `/api/filings/breakdown-timeline/?${params.toString()}`
  const response = await apiGet<FilingBreakdownTimeline>(url)
  return response
}

/**
 * Format seconds to human-readable time format
 * e.g., 540 seconds -> "9m", 3661 seconds -> "1h 1m"
 */
export function formatTimeToSubmission(seconds: number | null): string {
  if (seconds === null) return "—"

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours === 0) {
    return `${minutes}m`
  }

  if (minutes === 0) {
    return `${hours}h`
  }

  return `${hours}h ${minutes}m`
}

/**
 * Format rejection rate as percentage
 */
export function formatRejectionRate(rate: number): string {
  return `${rate.toFixed(1)}%`
}
